import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import {
  Alert, Button, Card, EmptyState, Field, Modal, Spinner, StarRating, StatusPill, formatDate, formatTime,
} from '../../components/ui';
import { useToast } from '../../components/Toast';

const CANCELLABLE = ['requested', 'assigned', 'confirmed'];
const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function seriesPatternLabel(series, t) {
  if (series.patternType === 'weekly') {
    const days = [...series.weekdays].sort().map((d) => t(`familyRequest.weekdays.${WEEKDAY_KEYS[d]}`)).join(', ');
    return `${days} · ${formatDate(series.startDate)} – ${formatDate(series.endDate)}`;
  }
  return `${t('familyBookings.selectedDates')} · ${formatDate(series.startDate)} – ${formatDate(series.endDate)}`;
}

function BookingCard({ booking, onCancel, onReport, onReview, t }) {
  const canReview = booking.status === 'completed' && booking.nanny && !booking.myReviewSubmitted;
  return (
    <article className="booking-card">
      <header>
        <div>
          <strong>{formatDate(booking.bookingDate, { long: true })}</strong>
          <span className="muted"> · {formatTime(booking.startTime)} – {formatTime(booking.endTime)}</span>
        </div>
        <StatusPill status={booking.status} label={booking.statusLabel} />
      </header>
      <dl className="detail-grid">
        <div>
          <dt>{t('familyBookings.yourNanny')}</dt>
          <dd>
            {booking.nanny ? (
              <>
                {booking.nanny.name}{' '}
                <Link to={`/family/nannies/${booking.nanny.id}`}>({t('familyBookings.viewNannyProfile')})</Link>
              </>
            ) : (
              t('familyBookings.beingMatched')
            )}
          </dd>
        </div>
        {booking.nanny && (
          <div><dt>{t('familyBookings.nannyContact')}</dt><dd>{booking.nanny.phone || booking.nanny.email}</dd></div>
        )}
        <div><dt>{t('familyBookings.children')}</dt><dd>{booking.childrenCount}</dd></div>
        <div><dt>{t('familyBookings.requirement')}</dt><dd>{booking.requirement || '—'}</dd></div>
        <div><dt>{t('familyBookings.reference')}</dt><dd>{booking.reference}</dd></div>
      </dl>
      {booking.notes && <p className="note-block"><strong>{t('familyBookings.yourNotes')}</strong> {booking.notes}</p>}
      {booking.status === 'cancelled' && booking.cancelReason && (
        <p className="note-block"><strong>{t('familyBookings.cancellationReason')}</strong> {booking.cancelReason}</p>
      )}
      {booking.status === 'completed' && booking.nanny && booking.myReviewSubmitted && (
        <p className="muted small">{t('familyBookings.reviewSubmitted')}</p>
      )}
      {(onCancel && CANCELLABLE.includes(booking.status)) || (onReport && booking.nanny) || (onReview && canReview) ? (
        <div className="form-footer">
          {onReview && canReview && (
            <Button type="button" variant="ghost" onClick={() => onReview(booking)}>
              {t('familyBookings.leaveReview')}
            </Button>
          )}
          {onReport && booking.nanny && (
            <Button type="button" variant="ghost" onClick={() => onReport(booking)}>
              {t('familyBookings.reportNanny')}
            </Button>
          )}
          {onCancel && CANCELLABLE.includes(booking.status) && (
            <Button type="button" variant="ghost" onClick={() => onCancel(booking)}>
              {t('familyBookings.cancelShift')}
            </Button>
          )}
        </div>
      ) : null}
    </article>
  );
}

function SeriesGroup({ series, bookings, onCancelShift, onCancelSeries, onReport, onReview, readOnly, t }) {
  const cancellableShifts = readOnly ? [] : bookings.filter((b) => CANCELLABLE.includes(b.status));
  return (
    <div className="series-group">
      <header>
        <div>
          <strong>{t('familyBookings.recurringBooking')}</strong>
          <span className="muted">{seriesPatternLabel(series, t)}</span>
        </div>
        <StatusPill
          status={series.status === 'cancelled' ? 'cancelled' : 'confirmed'}
          label={series.status === 'cancelled' ? t('familyBookings.seriesCancelled') : t('familyBookings.shiftsCount', { count: bookings.length, plural: bookings.length === 1 ? '' : 's' })}
        />
      </header>
      <ul className="series-shift-list">
        {bookings.map((b) => (
          <li key={b.id}>
            <span className="shift-info">
              <strong>{formatDate(b.bookingDate, { long: true })}</strong>
              <span className="muted">{formatTime(b.startTime)} – {formatTime(b.endTime)}</span>
              <StatusPill status={b.status} label={b.statusLabel} />
              {b.nanny && (
                <Link className="muted" to={`/family/nannies/${b.nanny.id}`}>{b.nanny.name}</Link>
              )}
            </span>
            {b.status === 'completed' && b.nanny && !b.myReviewSubmitted && onReview && (
              <Button type="button" variant="ghost" onClick={() => onReview(b)}>{t('familyBookings.leaveReview')}</Button>
            )}
            {b.nanny && onReport && (
              <Button type="button" variant="ghost" onClick={() => onReport(b)}>{t('familyBookings.reportNanny')}</Button>
            )}
            {!readOnly && CANCELLABLE.includes(b.status) && (
              <Button type="button" variant="ghost" onClick={() => onCancelShift(b)}>{t('familyBookings.cancelShiftShort')}</Button>
            )}
          </li>
        ))}
      </ul>
      {cancellableShifts.length > 0 && (
        <div className="form-footer">
          <Button type="button" variant="ghost" onClick={() => onCancelSeries(series)}>
            {t('familyBookings.cancelAllRemaining')}
          </Button>
        </div>
      )}
    </div>
  );
}

function ReviewModal({ booking, onClose, onSubmit, busy, t }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  function submit() {
    if (!rating) return;
    onSubmit({ rating, comment });
  }

  return (
    <Modal title={t('familyBookings.reviewModalTitle')} onClose={onClose}>
      <p className="muted">{t('familyBookings.reviewModalSubtitle')}</p>
      <StarRating value={rating} onChange={setRating} />
      <Field label={t('familyBookings.reviewCommentLabel')}>
        <textarea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('familyBookings.reviewCommentPlaceholder')}
        />
      </Field>
      <div className="form-footer">
        <Button type="button" onClick={submit} loading={busy} disabled={!rating}>
          {t('familyBookings.submitReview')}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          {t('familyBookings.cancel')}
        </Button>
      </div>
    </Modal>
  );
}

export default function FamilyBookings() {
  const { t } = useTranslation();
  const toast = useToast();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);

  useEffect(() => {
    if (location.state?.flash) toast.success(location.state.flash);
  }, [location.state, toast]);

  function load() {
    api.get('/api/family/bookings').then(setData).catch((err) => setError(err.message));
  }

  useEffect(load, []);

  async function cancelShift(booking) {
    const reason = window.prompt(t('familyBookings.cancelReasonPrompt'), '');
    if (reason === null) return;
    try {
      await api.post(`/api/family/bookings/${booking.id}/cancel`, { reason });
      toast.success(t('familyBookings.shiftCancelled'));
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function reportNanny(booking) {
    const message = window.prompt(t('familyBookings.reportPrompt'), '');
    if (!message) return;
    try {
      await api.post('/api/family/nanny-reports', { bookingId: booking.id, message });
      toast.success(t('familyBookings.reportSent'));
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function submitReview({ rating, comment }) {
    setReviewBusy(true);
    try {
      await api.post(`/api/family/bookings/${reviewBooking.id}/review`, { rating, comment });
      toast.success(t('familyBookings.reviewThanks'));
      setReviewBooking(null);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setReviewBusy(false);
    }
  }

  async function cancelSeries(series) {
    const ok = window.confirm(t('familyBookings.confirmCancelSeries'));
    if (!ok) return;
    const reason = window.prompt(t('familyBookings.cancelReasonPrompt'), '') || '';
    try {
      await api.post(`/api/family/bookings/series/${series.id}/cancel`, { reason });
      toast.success(t('familyBookings.seriesCancelledToast'));
      load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  if (!data) return error ? <Alert kind="error">{error}</Alert> : <Spinner />;

  function groupBySeries(list) {
    const seriesGroups = new Map();
    const standalone = [];
    for (const booking of list) {
      if (booking.series) {
        if (!seriesGroups.has(booking.series.id)) seriesGroups.set(booking.series.id, { series: booking.series, bookings: [] });
        seriesGroups.get(booking.series.id).bookings.push(booking);
      } else {
        standalone.push(booking);
      }
    }
    return { seriesGroups: [...seriesGroups.values()], standalone };
  }

  const upcomingGrouped = groupBySeries(data.upcoming);
  const pastGrouped = groupBySeries(data.past);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('familyBookings.pageTitle')}</h1>
          <p className="muted">{t('familyBookings.subtitle')}</p>
        </div>
        <Link className="btn btn-primary" to="/family/request">{t('familyBookings.requestNanny')}</Link>
      </div>


      <Card title={t('familyBookings.upcoming', { count: data.upcoming.length })}>
        {data.upcoming.length ? (
          <>
            {upcomingGrouped.seriesGroups.map((g) => (
              <SeriesGroup
                key={g.series.id}
                series={g.series}
                bookings={g.bookings}
                onCancelShift={cancelShift}
                onCancelSeries={cancelSeries}
                onReport={reportNanny}
                onReview={setReviewBooking}
                t={t}
              />
            ))}
            {upcomingGrouped.standalone.map((b) => (
              <BookingCard key={b.id} booking={b} onCancel={cancelShift} onReport={reportNanny} onReview={setReviewBooking} t={t} />
            ))}
          </>
        ) : (
          <EmptyState title={t('familyBookings.nothingBookedYet')}>
            <Link to="/family/request">{t('familyBookings.requestNanny')}</Link> {t('familyBookings.andWeWillHandle')}
          </EmptyState>
        )}
      </Card>

      <Card title={t('familyBookings.previousBookings', { count: data.past.length })}>
        {data.past.length ? (
          <>
            {pastGrouped.seriesGroups.map((g) => (
              <SeriesGroup
                key={g.series.id}
                series={g.series}
                bookings={g.bookings}
                onReport={reportNanny}
                onReview={setReviewBooking}
                readOnly
                t={t}
              />
            ))}
            {pastGrouped.standalone.map((b) => (
              <BookingCard key={b.id} booking={b} onReport={reportNanny} onReview={setReviewBooking} t={t} />
            ))}
          </>
        ) : (
          <EmptyState title={t('familyBookings.noPastBookings')} />
        )}
      </Card>

      {reviewBooking && (
        <ReviewModal
          booking={reviewBooking}
          onClose={() => setReviewBooking(null)}
          onSubmit={submitReview}
          busy={reviewBusy}
          t={t}
        />
      )}
    </>
  );
}
